import { ConfirmModal, ConfirmModalRef, Spinner, usePageNavigation } from '@shared/components';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductCategories } from '../../hooks/useProductCategories';
import { useProductCategoryMutations } from '../../hooks/useProductCategoryMutations';
import { CreateCategoryPayload, ProductCategory, UpdateCategoryPayload } from '../../models/ProductCategory';
import { CategoriesTable } from '../display/CategoriesTable';
import { CategoryFormDialog } from '../interaction/CategoryFormDialog';

export const Main = () => {
  const { t } = useTranslation('productCategoriesModule');
  const { hasPermission } = usePermissions();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const canManage = hasPermission('categories.manage');

  const [search, setSearch] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);

  const { data: categories = [], isLoading } = useProductCategories(search.trim() || undefined);
  const mutations = useProductCategoryMutations();

  const isFormDialogOpen = isCreateDialogOpen || editingCategory != null;

  const handleCreateCategory = (payload: CreateCategoryPayload) => {
    mutations.createCategory.mutate(payload, {
      onSuccess: () => setIsCreateDialogOpen(false),
    });
  };

  const handleUpdateCategory = (payload: UpdateCategoryPayload) => {
    if (!editingCategory) return;

    mutations.updateCategory.mutate({ id: editingCategory.id, payload }, { onSuccess: () => setEditingCategory(null) });
  };

  const handleDeleteCategory = (category: ProductCategory) => {
    confirmModalRef.current?.open(
      () => mutations.deleteCategory.mutateAsync(category.id),
      undefined,
      t('deleteDialog.title'),
      t('deleteDialog.message', { name: category.name }),
      t('deleteDialog.confirm'),
      t('deleteDialog.cancel'),
    );
  };

  const handleCloseFormDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingCategory(null);
  };

  const breadcrumbs = useMemo(() => [{ label: t('pageTitle') }], [t]);
  usePageNavigation({ breadcrumbs });

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <CategoriesTable
        categories={categories}
        search={search}
        onSearchChange={setSearch}
        canManage={canManage}
        onCreateClick={() => setIsCreateDialogOpen(true)}
        onEdit={setEditingCategory}
        onDelete={handleDeleteCategory}
      />

      {canManage && (
        <CategoryFormDialog
          isOpen={isFormDialogOpen}
          onClose={handleCloseFormDialog}
          onSubmit={(payload) => {
            if (editingCategory) {
              handleUpdateCategory(payload as UpdateCategoryPayload);
              return;
            }
            handleCreateCategory(payload as CreateCategoryPayload);
          }}
          category={editingCategory}
          isLoading={mutations.createCategory.isPending || mutations.updateCategory.isPending}
        />
      )}

      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
